/**
 * Grade Synchronization Hook
 * 
 * Listens to grading response messages (not grades collection) and triggers local grade processing.
 * Grades are now embedded in messages for instant UI updates without Firestore write delays.
 */

import { useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { RawGrade } from '../services/localGradeProcessor';
import { generateDeterministicGradeId } from '../utils/gradeIdGenerator';

export interface UseGradeSyncProps {
  conversationId: string | null;
  onGradesReceived: (grades: RawGrade[]) => void;
  onNewGrades: (grades: RawGrade[]) => void;
}

/**
 * Hook to sync grades from messages to local state
 */
/**
 * Filter grades to only those from the FINAL grading matrix round per prompt
 * Progressive grading creates multiple matrices - we only want the last/complete one
 * Multiple test runs of the same conversation create duplicates - keep only the most recent run
 * Uses gradingMessageId to identify the most recent set of grading messages
 */
// REMOVED: filterToFinalGradingRound() - time-based filtering was unnecessary
// userPromptMessageId already separates different prompts
// Test-retests should be preserved, not filtered

export const useGradeSync = ({
  conversationId,
  onGradesReceived,
  onNewGrades,
}: UseGradeSyncProps) => {
  const previousGradeIdsRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    // Only sync if we have a conversation
    if (!conversationId) {
      return;
    }

    // Reset state for new conversation
    // IMPORTANT: Clear previous grade IDs to force reprocessing of all grades
    // However, the localGradeProcessor will deduplicate any grades that were already processed
    const previousCount = previousGradeIdsRef.current.size;
    previousGradeIdsRef.current = new Set();
    isInitialLoadRef.current = true;
    
    console.log('🔄 useGradeSync: Reset for new conversation:', conversationId, {
      clearedGradeIds: previousCount,
      newRefSize: previousGradeIdsRef.current.size
    });

    // Listen to grading response messages for this conversation
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const q = query(
      messagesRef,
      where('isGradingResponse', '==', true),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const allGrades: RawGrade[] = [];
        const newGrades: RawGrade[] = [];

        const changes = snapshot.docChanges();
        console.log('🔍 useGradeSync snapshot:', {
          totalDocs: snapshot.docs.length,
          changesCount: changes.length,
          changeTypes: changes.map(c => `${c.type}`).join(', '),
          isInitialLoad: isInitialLoadRef.current
        });

        // On initial load, process ALL documents to handle refresh/navigation
        // On subsequent updates, only process 'added' changes for real-time updates
        const docsToProcess = isInitialLoadRef.current 
          ? snapshot.docs.map(doc => ({ doc, type: 'added' as const }))
          : snapshot.docChanges().filter(change => change.type === 'added');

        docsToProcess.forEach((change) => {
          const doc = change.doc;
          const data = doc.data();
          const messageId = doc.id;

          // Check for new raw grading data format (backend sends raw extraction)
          const rawGradingData = data.rawGradingData;
          if (rawGradingData) {
            // Extract and validate grades from raw Gemini extraction
            const extractedGrades = rawGradingData.extractedGrades;
            const candidateIndices = rawGradingData.candidateIndices;
            const candidateModelIds = rawGradingData.candidateModelIds;
            // CRITICAL: Explicit boolean check to avoid undefined
            const isSelfEval = rawGradingData.isSelfEval === true;
            // Self-grading metadata: which candidate position is the grader's own response
            const selfCandidateNum = rawGradingData.selfCandidateNum || 0;

            // Process each candidate's grade
            for (let i = 0; i < candidateIndices.length; i++) {
              const candidateNum = i + 1;
              const rawGrade = extractedGrades[`candidate${candidateNum}_grade`];

              // Normalize grade: trim whitespace, uppercase, remove prefixes
              let normalizedGrade = String(rawGrade || '').trim().toUpperCase();
              normalizedGrade = normalizedGrade.replace(/^[*\-•>\s]+/, '');

              // Validate grade is A/B/C/D/F
              const validGrades = ['A', 'B', 'C', 'D', 'F'];
              if (!validGrades.includes(normalizedGrade)) {
                console.warn('⚠️ Invalid grade extracted, skipping', {
                  candidateNum,
                  rawGrade,
                  normalizedGrade,
                  graderModelId: rawGradingData.graderModelId,
                  candidateModelId: candidateModelIds[i],
                  isSelfEval,
                });
                continue; // Skip invalid grades
              }

              // Extract creator from model ID
              const candidateModelId = candidateModelIds[i];
              const creator = candidateModelId.split('/')[0] || 'unknown';

              // NOTE: Backend fix deployed - userPromptMessageId now included on initial write
              // Old messages might not have it, but that's okay since they won't have duplicates

              // Create grade ID that allows test-retests (multiple grades from same grader to same receiver)
              // Include gradingMessageId to differentiate test-retests
              const gradeId = generateDeterministicGradeId(
                rawGradingData.conversationId,
                rawGradingData.userPromptMessageId,
                rawGradingData.graderModelId,
                candidateModelId,
                isSelfEval,
                messageId // Include messageId to differentiate test-retests
              );

              // Build RawGrade object
              const grade: RawGrade = {
                gradeId: gradeId,
                conversationId: rawGradingData.conversationId,
                userId: rawGradingData.userId,
                candidateModelId: candidateModelId,
                graderModelId: rawGradingData.graderModelId,
                grade: normalizedGrade,
                creator: creator,
                prioritySetting: rawGradingData.prioritySetting,
                isSelfEval: isSelfEval, // Use flag from rawGradingData
                isRetest: false,
                timestamp: data.timestamp?.toDate?.() || new Date(),
                userPromptMessageId: rawGradingData.userPromptMessageId,
                gradingMessageId: messageId, // Track which message generated this grade

                // Metadata fields
                aisNumber: rawGradingData.aisNumber,
                gradingPromptVersion: rawGradingData.gradingPromptVersion,
                ionoffVersion: rawGradingData.ionoffVersion,
                promptWordCount: rawGradingData.promptWordCount,
                reasoningSetting: rawGradingData.reasoningEffort,
                responseWordCount: Array.isArray(rawGradingData.responseWordCount)
                  ? rawGradingData.responseWordCount[i]
                  : rawGradingData.responseWordCount,
                selfevalPrehocSetting: rawGradingData.selfevalPrehocSetting,
                synthesisPromptVariant: rawGradingData.synthesisPromptVariant,
                synthesisPromptVersion: rawGradingData.synthesisPromptVersion,
                webGroundingSetting: rawGradingData.webGroundingSetting,
                fallbackGrader: rawGradingData.usedFallback || false,

                // Self-eval specific: ionoffEquals flag (marks which synthesis variant is the final ionoff)
                ionoffEquals: Array.isArray(rawGradingData.candidateIonoffEquals)
                  ? rawGradingData.candidateIonoffEquals[i]
                  : false,

                // 🧑 +Human feature fields
                isHumanResponse: Array.isArray(rawGradingData.candidateIsHumanResponse)
                  ? rawGradingData.candidateIsHumanResponse[i]
                  : false,
                humanResponseAuthor: Array.isArray(rawGradingData.candidateHumanUserIds)
                  ? rawGradingData.candidateHumanUserIds[i]
                  : null,
                plusHumanOn: rawGradingData.plusHumanOn || false,
                excludeFromGlobalLeaderboard: Array.isArray(rawGradingData.candidateExcludeFromGlobal)
                  ? rawGradingData.candidateExcludeFromGlobal[i]
                  : false,
                
                // Self-grading: true when grader graded its own response
                // selfCandidateNum is 1-based, candidateNum is also 1-based
                isSelfGrade: selfCandidateNum > 0 && candidateNum === selfCandidateNum,
              };

              // Debug logging for self-eval grades
              if (isSelfEval) {
                console.log('🔍 useGradeSync: Self-eval grade extracted', {
                  messageId: messageId.substring(0, 8),
                  candidateNum,
                  candidateModelId: grade.candidateModelId,
                  graderModelId: grade.graderModelId,
                  grade: grade.grade,
                });
              }

              allGrades.push(grade);

              // Track new grades with detailed logging
              const isDuplicate = previousGradeIdsRef.current.has(gradeId);
              console.log('🔍 useGradeSync: Processing grade', {
                gradeId: gradeId.substring(0, 70),
                grader: grade.graderModelId.substring(0, 25),
                candidate: grade.candidateModelId.substring(0, 25),
                messageId: messageId.substring(0, 20),
                isDuplicate,
                trackedIdsCount: previousGradeIdsRef.current.size
              });
              
              if (isDuplicate) {
                console.warn('🚫 useGradeSync: Duplicate grade detected!', {
                  gradeId: gradeId.substring(0, 60),
                  grader: grade.graderModelId.substring(0, 20),
                  candidate: grade.candidateModelId.substring(0, 20),
                  messageId: messageId.substring(0, 20)
                });
              } else {
                newGrades.push(grade);
                previousGradeIdsRef.current.add(gradeId);
              }
            }

            return; // Processed raw grading data, skip old format check
          }

          // NEW: Handle candidateMapping + JSON content format (current backend extraction format)
          const candidateMapping = data.candidateMapping;
          if (candidateMapping && Array.isArray(candidateMapping) && data.content) {
            try {
              // Parse grades from JSON content
              const gradesJson = JSON.parse(data.content);
              // CRITICAL: Use graderModelId field (original grader), NOT modelUsed (fallback model)
              const graderModelId = data.graderModelId || data.extractedFromModelId || data.modelUsed;
              
              // Process each candidate
              for (const mapping of candidateMapping) {
                const candidateNum = mapping.candidateNum;
                const candidateModelId = mapping.modelId;
                const rawGrade = gradesJson[`candidate${candidateNum}_grade`];
                
                // Normalize grade
                let normalizedGrade = String(rawGrade || '').trim().toUpperCase();
                normalizedGrade = normalizedGrade.replace(/^[*\-•>\s]+/, '');
                
                // Validate grade
                const validGrades = ['A', 'B', 'C', 'D', 'F'];
                if (!validGrades.includes(normalizedGrade)) {
                  console.warn('⚠️ Invalid grade in candidateMapping format, skipping', {
                    candidateNum,
                    rawGrade,
                    normalizedGrade,
                    graderModelId,
                    candidateModelId,
                  });
                  continue;
                }
                
                // Extract creator
                const creator = candidateModelId.split('/')[0] || 'unknown';
                
                // NOTE: Backend fix deployed - userPromptMessageId now included on initial write
                // Old messages might not have it, use fallback to avoid losing data
                const userPromptMsgId = data.userPromptMessageId || 'legacy-' + doc.id;
                
                // Create deterministic, globally unique grade ID
                // CRITICAL: Ensure isSelfEval is always a boolean, never undefined
                const isSelfEvalGrade = mapping.isSelf === true;
                const gradeId = generateDeterministicGradeId(
                  conversationId,
                  userPromptMsgId,
                  graderModelId,
                  candidateModelId,
                  isSelfEvalGrade
                );
                
                // Build RawGrade object (minimal - missing metadata fields)
                // Try to get metadata from message data, fallback to 'unknown'/'default'
                const grade: RawGrade = {
                  gradeId: gradeId,
                  conversationId: conversationId, // From useEffect parameter
                  userId: data.userId || 'unknown',
                  candidateModelId: candidateModelId,
                  graderModelId: graderModelId,
                  grade: normalizedGrade,
                  creator: creator,
                  prioritySetting: data.prioritySetting || data.prioritize || 'intelligence', // Use from message or default to intelligence
                  isSelfEval: mapping.isSelf === true, // CRITICAL: Explicit boolean check
                  isRetest: false,
                  timestamp: data.timestamp?.toDate?.() || new Date(),
                  userPromptMessageId: data.userPromptMessageId || 'unknown',
                  gradingMessageId: messageId, // Track which message generated this grade
                  
                  // Metadata fields (set to defaults for now)
                  aisNumber: 3,
                  gradingPromptVersion: 'unknown',
                  ionoffVersion: 'unknown',
                  promptWordCount: 0,
                  reasoningSetting: 'default',
                  responseWordCount: 0,
                  selfevalPrehocSetting: false,
                  synthesisPromptVariant: 'unknown',
                  synthesisPromptVersion: 'unknown',
                  webGroundingSetting: false,
                  fallbackGrader: false,
                  
                  // +Human fields
                  isHumanResponse: false,
                  humanResponseAuthor: undefined,
                  plusHumanOn: false,
                  excludeFromGlobalLeaderboard: false,
                };
                
                console.log('🔍 useGradeSync: Grade extracted from candidateMapping format', {
                  messageId: messageId.substring(0, 8),
                  candidateNum,
                  candidateModelId: grade.candidateModelId,
                  graderModelId: grade.graderModelId,
                  grade: grade.grade,
                });
                
                allGrades.push(grade);
                
                // Track new grades
                if (!previousGradeIdsRef.current.has(gradeId)) {
                  newGrades.push(grade);
                  previousGradeIdsRef.current.add(gradeId);
                }
              }
              
              return; // Processed candidateMapping format
            } catch (error) {
              console.error('❌ Failed to parse candidateMapping grading message', {
                messageId: messageId.substring(0, 8),
                error: error instanceof Error ? error.message : String(error),
              });
              return;
            }
          }

          // Fallback: Check for old gradeData format (self-eval still uses this)
          const gradeData = data.gradeData;
          if (!gradeData) {
            return; // Skip messages without grade data
          }

          // Extract creator from model ID
          const candidateModelId = gradeData.modelId;
          const creator = candidateModelId?.split('/')[0] || gradeData.creator || 'unknown';

          // NOTE: Backend fix deployed - userPromptMessageId now included on initial write
          // For old messages without it, use fallback to avoid losing data
          if (!gradeData.userPromptMessageId || gradeData.userPromptMessageId === 'unknown') {
            gradeData.userPromptMessageId = 'legacy-' + messageId;
          }

          // Create deterministic, globally unique grade ID
          const gradeId = generateDeterministicGradeId(
            gradeData.conversationId,
            gradeData.userPromptMessageId,
            gradeData.graderModelId,
            candidateModelId,
            gradeData.isSelfEval || false
          );

          // Convert to RawGrade format
          const grade: RawGrade = {
            gradeId: gradeId,
            conversationId: gradeData.conversationId,
            userId: gradeData.userId,
            candidateModelId: candidateModelId,
            graderModelId: gradeData.graderModelId,
            grade: gradeData.grade,
            creator: creator,
            prioritySetting: gradeData.prioritySetting,
            isSelfEval: gradeData.isSelfEval || false,
            isRetest: gradeData.isRetest || false,
            timestamp: gradeData.timestamp?.toDate?.() || new Date(gradeData.timestamp),
            userPromptMessageId: gradeData.userPromptMessageId,
            gradingMessageId: messageId, // Track which message generated this grade

            // Metadata fields
            aisNumber: gradeData.aisNumber,
            gradingPromptVersion: gradeData.gradingPromptVersion,
            ionoffVersion: gradeData.ionoffVersion,
            promptWordCount: gradeData.promptWordCount,
            reasoningSetting: gradeData.reasoningSetting,
            responseWordCount: gradeData.responseWordCount,
            selfevalPrehocSetting: gradeData.selfevalPrehocSetting,
            synthesisPromptVariant: gradeData.synthesisPromptVariant,
            synthesisPromptVersion: gradeData.synthesisPromptVersion,
            webGroundingSetting: gradeData.webGroundingSetting,
            fallbackGrader: gradeData.fallbackGrader,
          };

          // Debug logging for self-eval grades
          if (gradeData.isSelfEval) {
            console.log('🔍 useGradeSync: Self-eval grade received', {
              messageId: messageId.substring(0, 8),
              gradeDataIsSelfEval: gradeData.isSelfEval,
              gradeIsSelfEval: grade.isSelfEval,
              candidateModelId: grade.candidateModelId,
              graderModelId: grade.graderModelId,
              grade: grade.grade,
            });
          }

          allGrades.push(grade);

          // Track new grades
          if (!previousGradeIdsRef.current.has(gradeId)) {
            newGrades.push(grade);
            previousGradeIdsRef.current.add(gradeId);
          }
        });

        // On initial load, process all grades
        if (isInitialLoadRef.current) {
          // Process all grades - no filtering needed
          // userPromptMessageId already separates different prompts
          // Test-retests (multiple grades from same grader to same gradee) are preserved
          console.log('📊 Initial grade sync:', {
            conversationId: conversationId.substring(0, 8),
            totalGrades: allGrades.length,
            peerGrades: allGrades.filter(g => !g.isSelfEval).length,
            selfEvalGrades: allGrades.filter(g => g.isSelfEval).length
          });
          onGradesReceived(allGrades);
          isInitialLoadRef.current = false;
        } else if (newGrades.length > 0) {
          // On subsequent updates, only process new grades
          console.log('📊 New grades received (REAL-TIME):', {
            conversationId: conversationId.substring(0, 8),
            newGrades: newGrades.length,
            peerGrades: newGrades.filter(g => g.isSelfEval === false).length,
            selfEvalGrades: newGrades.filter(g => g.isSelfEval === true).length,
            undefinedSelfEval: newGrades.filter(g => g.isSelfEval === undefined).length,
            totalDocsInSnapshot: snapshot.docs.length,
            previousGradeIds: previousGradeIdsRef.current.size
          });
          
          // Log each grade being added
          newGrades.forEach((g, idx) => {
            console.log(`  📍 Grade ${idx + 1}: ${g.graderModelId} → ${g.candidateModelId}: ${g.grade}`);
          });
          
          console.log('🎯 Calling onNewGrades with', newGrades.length, 'grades');
          onNewGrades(newGrades);
        } else {
          console.log('⏭️ Snapshot received but no new grades', {
            conversationId: conversationId.substring(0, 8),
            totalDocs: snapshot.docs.length,
            alreadyTracked: previousGradeIdsRef.current.size,
            isInitial: isInitialLoadRef.current
          });
        }
        
        console.log('✅ useGradeSync snapshot processing complete', {
          totalDocsProcessed: snapshot.docs.length,
          newGradesFound: newGrades.length,
          allGradesCount: allGrades.length
        });
      },
      (error) => {
        console.error('❌ Grade sync error:', error);
      }
    );

    return () => {
      unsubscribe();
      // CRITICAL: Clear the ref on cleanup to prevent stale grade IDs from persisting
      previousGradeIdsRef.current = new Set();
      console.log('🧹 useGradeSync: Cleaned up for conversation:', conversationId);
    };
  }, [conversationId, onGradesReceived, onNewGrades]);
};

